import {Component, DestroyRef, inject, Input} from '@angular/core';
import {FormGroup, ReactiveFormsModule} from "@angular/forms";
import {TitleCasePipe} from "@angular/common";
import {MaterialModule} from "../../../../material.module";
import {ToastService} from "../../../../services/layout/toast.service";
import {ClientService} from "../../../../services/auth/client.service";
import {ConfirmDialogComponent} from "../../../confirm-dialog/confirm-dialog.component";
import {IAssistantOptions} from "../../../../models/assistant-options.model";
import {AssistantsService} from "../../../../services/objects/assistants.service";
import {MatDialog} from "@angular/material/dialog";
import {Marked} from "marked";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {LlmModelsService} from "../../../../services/ai/llm-models.service";
import {buildLlmModelOptions, LlmModelOption, modelValueToLabel} from "../../../../utils/llm-models.utils";
import {isGpt4FamilyOpenAiModel} from "../../../../models/models.model";
import {
    getReasoningEffortDefault,
    getReasoningEffortOptions,
    isReasoningModel,
    REASONING_SUMMARY_OPTIONS
} from "../../../../utils/reasoning.utils";

@Component({
  selector: 'app-assistant-general-settings',
  templateUrl: './assistant-general-settings.component.html',
  styleUrls: ['./assistant-general-settings.component.scss'],
  providers: [Marked],
    imports: [MaterialModule, ReactiveFormsModule, TitleCasePipe]
})
export class AssistantGeneralSettingsComponent {
    clientService = inject(ClientService);
    private snackBar = inject(ToastService);
    private assistantsService = inject(AssistantsService);
    private dialog = inject(MatDialog);
    private markedService = inject(Marked);
    private destroyRef = inject(DestroyRef);
    private llmModelsService = inject(LlmModelsService);

  @Input() settingsForm!: FormGroup;
  @Input() assistant: IAssistantOptions | null = null;
  @Input() verifiedDomains: string[] = [];
  @Input() isDomainsLoading: boolean = false;

    isProposing = false;
    readonly summaryOptions = REASONING_SUMMARY_OPTIONS;
    readonly defaultRealtimeVoiceOptions: string[] = [
        'marin',
        'cedar',
        'alloy',
        'ash',
        'ballad',
        'coral',
        'echo',
        'sage',
        'shimmer',
        'verse'
    ];
    realtimeVoiceOptions: string[] = [...this.defaultRealtimeVoiceOptions];

    modelOptions: LlmModelOption[] = buildLlmModelOptions(null);

    displayModelValue = (value: string | null | undefined): string => modelValueToLabel(value, this.modelOptions);

    get legacyModelValue(): string | null {
        const raw = `${this.settingsForm?.get('model')?.value ?? ''}`.trim();
        if (!isGpt4FamilyOpenAiModel(raw)) return null;

        return this.modelOptions.some(option => option.value === raw) ? null : raw;
    }

    effectiveProvider(): string {
        const raw = `${this.settingsForm?.get('model')?.value ?? ''}`.trim();
        const configured = this.modelOptions.find(option => option.value === raw)?.provider;
        if (configured === 'anthropic') return 'Anthropic';
        if (configured === 'gemini') return 'Gemini';
        if (configured === 'openai') return 'OpenAI';

        const normalized = raw.toLowerCase();
        if (normalized.startsWith('anthropic:') || normalized.startsWith('claude')) return 'Anthropic';
        if (normalized.startsWith('gemini:') || normalized.startsWith('gemini') ||
            normalized.startsWith('models/gemini')) return 'Gemini';
        return 'OpenAI';
    }

  ngOnInit(): void {
    // Ensure the settingsForm has all necessary controls
    if (!this.settingsForm) {
      throw new Error('settingsForm Input is required for GeneralSettingsComponent');
    }
      const modelControl = this.settingsForm.get('model');
      const realTimeEnabledControl = this.settingsForm.get('realTimeEnabled');
      const realTimeVoiceControl = this.settingsForm.get('realTimeVoice');
      if (modelControl) {
          this.applyReasoningDefaults(modelControl.value);
          modelControl.valueChanges
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(value => {
                  this.applyReasoningDefaults(value);
              });
      }

      if (realTimeVoiceControl) {
          this.ensureRealtimeVoiceOption(realTimeVoiceControl.value);
          realTimeVoiceControl.valueChanges
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(value => {
                  this.ensureRealtimeVoiceOption(value);
                  if (realTimeEnabledControl?.value === true && !(value ?? '').toString().trim()) {
                      this.applyRealtimeVoiceDefault(true);
                  }
              });
      }

      if (realTimeEnabledControl && realTimeVoiceControl) {
          this.applyRealtimeVoiceDefault(realTimeEnabledControl.value);
          realTimeEnabledControl.valueChanges
              .pipe(takeUntilDestroyed(this.destroyRef))
              .subscribe(enabled => {
                  this.applyRealtimeVoiceDefault(enabled);
              });
      }

      this.llmModelsService.getModels()
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe((resp) => {
              this.modelOptions = buildLlmModelOptions(resp);
          });
  }

  proposeImprovements() {
    if (!this.assistant) return;
    this.isProposing = true;

    this.snackBar.showInfo('Proposing improvements...', 3000);
    this.assistantsService.proposeImprovementsToAssistantAsync(this.assistant.id).subscribe(data => {
      const dialogRef = this.dialog.open(ConfirmDialogComponent, {
        width: '450px',
        data: {
          title: 'Proposed changes to instructions',
          message: this.markedService.parse(data['Instructions']) as string,
          info: this.markedService.parse(data['Summary_of_proposed_changes']) as string,
          confirmText: 'Do you want to apply the proposed changed?',
          confirmButtonText: 'Update',
          cancelButtonText: 'Cancel'
        }
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result && this.assistant) {
          this.settingsForm.get('instructions')?.setValue(data['Instructions']);
          this.settingsForm.get('instructions')?.markAsDirty();
          this.settingsForm.markAsTouched();
        } else {
          this.snackBar.showInfo('Proposed changes were not applied.', 3000);
        }
      });
      this.isProposing = false;
    }, error => {
      console.error('Error proposing improvements', error);
      this.snackBar.showError('Error proposing improvements.', 3000);
      this.isProposing = false;
    });
  }

    shouldShowReasoningControls(): boolean {
        return isReasoningModel(this.settingsForm?.get('model')?.value);
    }

    getReasoningEffortOptionsForModel(): string[] {
        return getReasoningEffortOptions(this.settingsForm?.get('model')?.value);
    }

    private applyReasoningDefaults(model: string | null | undefined): void {
        const effortControl = this.settingsForm.get('reasoningEffort');
        const summaryControl = this.settingsForm.get('reasoningSummaryVerbosity');

        if (!effortControl || !summaryControl) {
            return;
        }

        if (!isReasoningModel(model)) {
            effortControl.setValue(getReasoningEffortDefault(null), {emitEvent: false});
            summaryControl.setValue('', {emitEvent: false});
            return;
        }

        const allowedEfforts = getReasoningEffortOptions(model);
        const currentValue = effortControl.value as string;
        if (!currentValue || !allowedEfforts.includes(currentValue)) {
            effortControl.setValue(getReasoningEffortDefault(model), {emitEvent: false});
        }
    }

    private applyRealtimeVoiceDefault(realTimeEnabled: boolean): void {
        if (!realTimeEnabled) {
            return;
        }

        const voiceControl = this.settingsForm.get('realTimeVoice');
        if (!voiceControl) {
            return;
        }

        const currentValue = (voiceControl.value ?? '').toString().trim();
        if (currentValue) {
            this.ensureRealtimeVoiceOption(currentValue);
            return;
        }

        voiceControl.setValue(this.defaultRealtimeVoiceOptions[0], {emitEvent: false});
        this.ensureRealtimeVoiceOption(this.defaultRealtimeVoiceOptions[0]);
    }

    private ensureRealtimeVoiceOption(value: string | null | undefined): void {
        const normalized = (value ?? '').toString().trim();
        if (!normalized) {
            this.realtimeVoiceOptions = [...this.defaultRealtimeVoiceOptions];
            return;
        }

        const existsInDefault = this.defaultRealtimeVoiceOptions.some(option => option === normalized);
        this.realtimeVoiceOptions = existsInDefault
            ? [...this.defaultRealtimeVoiceOptions]
            : [normalized, ...this.defaultRealtimeVoiceOptions];
    }
}
